import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import { RemovableDashedTag } from '@/components/RemovableDashedTag';
import { useMaskingUserType } from '@/hooks/useMaskingUserType';
import { useStatisticsBatch } from '@/hooks/useStatisticsBatch';
import { cn } from '@/utils/classname';
import { filterInternalTags } from '@/utils/userDisplay.util';

export type TagEntity = 'asset' | 'risk';

export const AddTag = ({
  open,
  onClose,
  disabled,
  description = 'Tags help organize and classify assets in your attack surface.',
  onSubmit,
  entity = 'asset',
}: {
  open: boolean;
  onClose: () => void;
  disabled: boolean;
  description?: string;
  onSubmit: (tag: string) => void;
  /**
   * Which entity's existing tag statistics to surface as suggestions.
   * Asset and risk services persist a `#statistic#<entity>#tags#now#<tag>`
   * record on every PUT that includes tags, and the dialog reads that list
   * here so the user can pick an existing tag or create a new one.
   */
  entity?: TagEntity;
}) => {
  const [tagValue, setTagValue] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const statisticKey = `${entity}#tags`;
  const { data: statsByKey, isLoading } = useStatisticsBatch({
    statisticKeys: [statisticKey],
    enabled: open,
  });

  // Hide internal hunt/agent/cato provenance tags from customer suggestions
  // (Praetorian sees all). These backend-managed tags are written only to
  // Risk.Tags, so masking applies to risk suggestions only — asset tags are
  // always customer-created, and a colliding name like 'hunt' is legitimate.
  const maskingUserType = useMaskingUserType();

  const existingTags = useMemo<string[]>(() => {
    const stats = statsByKey?.[statisticKey] ?? [];
    const seen = new Set<string>();
    for (const stat of stats) {
      if (!stat.value || seen.has(stat.value)) continue;
      seen.add(stat.value);
    }
    const visible =
      entity === 'risk'
        ? filterInternalTags([...seen], maskingUserType)
        : [...seen];
    return visible.sort((a, b) => a.localeCompare(b));
  }, [statsByKey, statisticKey, maskingUserType, entity]);

  const trimmed = tagValue.trim();
  const lowerFilter = filterValue.trim().toLowerCase();

  const filteredTags = useMemo(() => {
    if (!lowerFilter) return existingTags;
    return existingTags.filter(t => t.toLowerCase().includes(lowerFilter));
  }, [existingTags, lowerFilter]);

  function reset() {
    setTagValue('');
    setFilterValue('');
    onClose();
  }

  async function handleApply(tag: string) {
    const value = tag.trim();
    if (!value) return;
    await onSubmit(value);
    reset();
  }

  return (
    <Modal
      title="Add Tag"
      open={open}
      onClose={onClose}
      size="xl"
      footer={{
        text: 'Add',
        form: 'custom-tag-form',
        disabled: disabled || !trimmed,
      }}
    >
      <p className="text-chariot-text-secondary mb-6 text-sm">{description}</p>

      {/* Create new tag */}
      <section aria-labelledby="create-new-tag-heading" className="mb-6">
        <h3
          id="create-new-tag-heading"
          className="text-chariot-text-primary mb-2 text-sm font-semibold"
        >
          Create a new tag
        </h3>
        <form
          id="custom-tag-form"
          className="flex items-stretch gap-2"
          onSubmit={async event => {
            event.preventDefault();
            await handleApply(trimmed);
          }}
        >
          <label htmlFor="custom-tag-input" className="sr-only">
            Tag
          </label>
          <input
            id="custom-tag-input"
            type="text"
            value={tagValue}
            onChange={e => setTagValue(e.target.value)}
            placeholder="e.g. production"
            autoComplete="off"
            autoFocus
            disabled={disabled}
            className={cn(
              'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
              'placeholder:text-chariot-text-tertiary flex-1 rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden',
              'disabled:opacity-50'
            )}
          />
        </form>
      </section>

      {/* Existing tags */}
      <section aria-labelledby="existing-tags-heading">
        <div className="border-chariot-stroke mb-3 flex items-center justify-between border-t pt-4">
          <h3
            id="existing-tags-heading"
            className="text-chariot-text-primary text-sm font-semibold"
          >
            Apply an existing tag
          </h3>
          {existingTags.length > 0 && (
            <span className="text-chariot-text-tertiary text-xs">
              {existingTags.length} {existingTags.length === 1 ? 'tag' : 'tags'}
            </span>
          )}
        </div>

        {existingTags.length > 8 && (
          <div className="relative mb-3">
            <MagnifyingGlassIcon
              aria-hidden="true"
              className="text-chariot-text-tertiary pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            />
            <input
              type="search"
              value={filterValue}
              onChange={e => setFilterValue(e.target.value)}
              placeholder="Filter tags"
              aria-label="Filter existing tags"
              className={cn(
                'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
                'placeholder:text-chariot-text-tertiary w-full rounded-md border py-1.5 pr-3 pl-8 text-sm',
                'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
              )}
            />
          </div>
        )}

        <div
          className={cn(
            'border-chariot-stroke bg-chariot-background-secondary rounded-md border p-3',
            'max-h-64 overflow-auto'
          )}
        >
          {isLoading ? (
            <p className="text-chariot-text-secondary text-sm">
              Loading existing tags…
            </p>
          ) : existingTags.length === 0 ? (
            <p className="text-chariot-text-secondary text-sm">
              No tags yet — create one above to get started.
            </p>
          ) : filteredTags.length === 0 ? (
            <p className="text-chariot-text-secondary text-sm">
              No tags match &ldquo;{filterValue}&rdquo;.
            </p>
          ) : (
            <div
              role="list"
              aria-label="Existing tags"
              className="flex flex-wrap gap-2"
            >
              {filteredTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  role="listitem"
                  disabled={disabled}
                  onClick={() => handleApply(tag)}
                  data-testid={`existing-tag-${tag}`}
                  className={cn(
                    'border-chariot-stroke text-chariot-text-secondary inline-flex items-center gap-1.5',
                    'rounded-full border-2 border-dashed px-3 py-1.5 text-xs leading-3 font-semibold uppercase',
                    'hover:border-chariot-brand-primary hover:text-chariot-brand-primary',
                    'focus:border-chariot-brand-primary focus:text-chariot-brand-primary focus:outline-hidden',
                    'transition-colors',
                    'disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                >
                  <PlusIcon aria-hidden="true" className="size-3" />
                  <span className="truncate">{tag}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </Modal>
  );
};

export const SimpleTags = ({
  tags,
  onRemove,
  variant = 'default',
}: {
  tags: string[];
  onRemove?: (tag: string) => void;
  variant?: 'default' | 'compact';
}) => {
  if (tags.length === 0) return null;

  // Compact variant for drawer headers - smaller, solid Chariot purple
  if (variant === 'compact') {
    return (
      <div
        className="flex flex-wrap gap-2"
        role="list"
        aria-label="Custom tags"
      >
        {tags?.map((tag, index) =>
          onRemove ? (
            <RemovableDashedTag
              key={`${tag}-${index}`}
              label={tag}
              size="xs"
              onRemove={() => onRemove(tag)}
              className="border-chariot-brand-primary bg-chariot-brand-primary hover:bg-chariot-brand-primary/80 hover:border-chariot-brand-primary/80 border-solid text-white"
            />
          ) : (
            <span
              key={`${tag}-${index}`}
              role="listitem"
              className="border-chariot-brand-primary bg-chariot-brand-primary inline-flex items-center rounded-full border-2 border-solid px-2 py-1 text-[10px] leading-3 font-semibold text-white uppercase"
            >
              {tag}
            </span>
          )
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="flex flex-wrap gap-2" role="list" aria-label="Custom tags">
      {tags?.map((tag, index) =>
        onRemove ? (
          <RemovableDashedTag
            key={`${tag}-${index}`}
            label={tag}
            size="sm"
            onRemove={() => onRemove(tag)}
          />
        ) : (
          <span
            key={`${tag}-${index}`}
            role="listitem"
            className="border-chariot-stroke text-chariot-text-secondary inline-flex items-center rounded-full border-2 border-dashed px-3 py-1.5 text-xs leading-3 font-semibold uppercase"
          >
            {tag}
          </span>
        )
      )}
    </div>
  );
};
